import { useState, useEffect, useMemo } from "react";
import { DashboardLayout } from "@/components/Dashboard/DashboardLayout";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useScannerContext } from "@/contexts/ScannerContext";
import { useNFCReader } from "@/hooks/useNFCReader";
import { api } from "@/lib/apiClient";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Clock, LogIn, LogOut, Coffee, CheckCircle2, AlertCircle, Nfc } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

interface AttendanceEvent {
  id: string;
  user_id: string;
  event_type: 'clock_in' | 'clock_out' | 'break_start' | 'break_end';
  event_at: string;
  source: string;
  notes: string | null;
  is_corrected: boolean;
}

const getEventLabels = (t: any) => ({
  clock_in: { label: t.timeTracking.clockIn, icon: LogIn, color: "bg-green-500" },
  clock_out: { label: t.timeTracking.clockOut, icon: LogOut, color: "bg-red-500" },
  break_start: { label: t.timeTracking.breakStart, icon: Coffee, color: "bg-amber-500" },
  break_end: { label: t.timeTracking.breakEnd, icon: CheckCircle2, color: "bg-blue-500" },
});

export default function Fichaje() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const eventLabels = getEventLabels(t);
  const { nfcBridge } = useScannerContext();
  const { isSupported: isMobileNfcSupported, scanTagUid } = useNFCReader();
  const queryClient = useQueryClient();
  const [now, setNow] = useState(new Date());

  // Update clock every second
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Fetch today's events for current user
  const todayStart = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d.toISOString();
  }, []);

  const { data: todayEvents = [], isLoading } = useQuery<AttendanceEvent[]>({
    queryKey: ["attendance-today", user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      const { data, error } = await api
        .from("attendance_events")
        .select("*")
        .eq("user_id", user.id)
        .gte("event_at", todayStart)
        .order("event_at", { ascending: true });
      if (error) throw error;
      return (data || []) as AttendanceEvent[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // refresh each 30s
  });

  // Compute current state (working / on_break / off)
  const currentState = useMemo(() => {
    const events = todayEvents.filter(e => !e.is_corrected);
    if (events.length === 0) return "off";
    const last = events[events.length - 1];
    switch (last.event_type) {
      case "clock_in": return "working";
      case "break_start": return "on_break";
      case "break_end": return "working";
      case "clock_out": return "off";
      default: return "off";
    }
  }, [todayEvents]);

  // Compute hours worked today
  const hoursToday = useMemo(() => {
    const events = todayEvents.filter(e => !e.is_corrected);
    let workMs = 0;
    let lastClockIn: number | null = null;
    let lastBreakStart: number | null = null;
    let breakMs = 0;
    for (const e of events) {
      const ts = new Date(e.event_at).getTime();
      if (e.event_type === "clock_in") lastClockIn = ts;
      else if (e.event_type === "clock_out" && lastClockIn) {
        workMs += ts - lastClockIn - breakMs;
        lastClockIn = null;
        breakMs = 0;
      }
      else if (e.event_type === "break_start") lastBreakStart = ts;
      else if (e.event_type === "break_end" && lastBreakStart) {
        breakMs += ts - lastBreakStart;
        lastBreakStart = null;
      }
    }
    // If currently working, count up to now
    if (currentState === "working" && lastClockIn) {
      workMs += now.getTime() - lastClockIn - breakMs - (lastBreakStart ? now.getTime() - lastBreakStart : 0);
    }
    return Math.max(0, workMs);
  }, [todayEvents, now, currentState]);

  // Mutation: register event
  const registerMutation = useMutation({
    mutationFn: async (params: { event_type: AttendanceEvent['event_type']; source?: 'button' | 'nfc' }) => {
      if (!user?.id) throw new Error("No autenticado");
      const { error } = await api.from("attendance_events").insert({
        user_id: user.id,
        event_type: params.event_type,
        source: params.source || 'button',
        device_info: {
          userAgent: navigator.userAgent.substring(0, 200),
          platform: navigator.platform,
        },
      });
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      const lbl = eventLabels[vars.event_type]?.label || vars.event_type;
      toast.success(`✅ ${lbl} ${t.timeTracking.messages.eventRegistered} — ${format(new Date(), "HH:mm:ss")}`);
      queryClient.invalidateQueries({ queryKey: ["attendance-today", user?.id] });
    },
    onError: (e: any) => toast.error(`${t.timeTracking.messages.errorRegistering}: ${e.message}`),
  });

  // NFC-based fichaje: scan card → check if it matches a user → register the toggle event
  const handleNfcFichaje = async (uid: string) => {
    try {
      const { data: tag } = await api
        .from("nfc_tags")
        .select("tag_uid, tag_label, assigned_to_user_id, tag_type")
        .eq("tag_uid", uid)
        .maybeSingleRow();
      if (!tag) {
        toast.error(`Chip ${uid}: ${t.timeTracking.nfc.chipNotRegistered}`);
        return;
      }
      if (!(tag as any).assigned_to_user_id) {
        toast.error(`Chip ${uid}: ${t.timeTracking.nfc.chipNotAssigned}`);
        return;
      }
      const userId = (tag as any).assigned_to_user_id;
      // Determine current state for that user
      const { data: lastEvent } = await api
        .from("attendance_events")
        .select("event_type, event_at")
        .eq("user_id", userId)
        .gte("event_at", todayStart)
        .order("event_at", { ascending: false })
        .limit(1)
        .maybeSingleRow();
      const last = (lastEvent as any)?.event_type;
      let nextEvent: AttendanceEvent['event_type'] = "clock_in";
      if (last === "clock_in" || last === "break_end") nextEvent = "clock_out";
      else if (last === "clock_out") nextEvent = "clock_in";
      else if (last === "break_start") nextEvent = "break_end";
      const { error } = await api.from("attendance_events").insert({
        user_id: userId,
        event_type: nextEvent,
        source: 'nfc',
        notes: `NFC: ${uid}`,
      });
      if (error) throw error;
      toast.success(`NFC ${uid}: ${eventLabels[nextEvent].label} ${t.timeTracking.messages.eventRegistered}`);
      queryClient.invalidateQueries({ queryKey: ["attendance-today"] });
    } catch (e: any) {
      toast.error(`${t.timeTracking.messages.errorNfc}: ${e.message}`);
    }
  };

  // Listen for USB NFC reads
  const [lastUsbReadAt, setLastUsbReadAt] = useState(0);
  useEffect(() => {
    if (nfcBridge.lastRead && nfcBridge.lastRead.timestamp > lastUsbReadAt) {
      setLastUsbReadAt(nfcBridge.lastRead.timestamp);
      handleNfcFichaje(nfcBridge.lastRead.uid);
    }
  }, [nfcBridge.lastRead, lastUsbReadAt]);

  const handleScanMobile = async () => {
    const result = await scanTagUid();
    if (result?.serialNumber) {
      await handleNfcFichaje(result.serialNumber);
    }
  };

  const formatHours = (ms: number) => {
    const h = Math.floor(ms / 3600000);
    const m = Math.floor((ms % 3600000) / 60000);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
  };

  const stateBadge = () => {
    if (currentState === "working") return <Badge className="bg-green-500 text-white">{t.timeTracking.currentState.working}</Badge>;
    if (currentState === "on_break") return <Badge className="bg-amber-500 text-white">{t.timeTracking.currentState.onBreak}</Badge>;
    return <Badge variant="outline">{t.timeTracking.currentState.off}</Badge>;
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">{t.timeTracking.title}</h1>
            <p className="text-muted-foreground">{t.timeTracking.subtitle}</p>
          </div>
        </div>

        {/* Estado actual */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span className="flex items-center gap-2"><Clock className="w-5 h-5" /> {t.timeTracking.state}</span>
              {stateBadge()}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-muted-foreground">{t.timeTracking.now}</p>
                <p className="text-2xl font-mono font-bold">{format(now, "HH:mm:ss")}</p>
              </div>
              <div>
                <p className="text-sm text-muted-foreground">{t.timeTracking.hoursToday}</p>
                <p className="text-2xl font-mono font-bold">{formatHours(hoursToday)}</p>
              </div>
            </div>

            {/* Botones */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
              {currentState === "off" && (
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 h-20 text-lg"
                  onClick={() => registerMutation.mutate({ event_type: "clock_in" })}
                  disabled={registerMutation.isPending}
                >
                  <LogIn className="w-6 h-6 mr-2" /> {t.timeTracking.buttons.clockIn}
                </Button>
              )}
              {currentState === "working" && (
                <>
                  <Button
                    size="lg"
                    className="bg-red-600 hover:bg-red-700 h-20 text-lg"
                    onClick={() => registerMutation.mutate({ event_type: "clock_out" })}
                    disabled={registerMutation.isPending}
                  >
                    <LogOut className="w-6 h-6 mr-2" /> {t.timeTracking.buttons.clockOut}
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="h-20 text-lg border-amber-500 text-amber-700 hover:bg-amber-50"
                    onClick={() => registerMutation.mutate({ event_type: "break_start" })}
                    disabled={registerMutation.isPending}
                  >
                    <Coffee className="w-6 h-6 mr-2" /> {t.timeTracking.buttons.startBreak}
                  </Button>
                </>
              )}
              {currentState === "on_break" && (
                <Button
                  size="lg"
                  className="bg-blue-600 hover:bg-blue-700 h-20 text-lg col-span-2"
                  onClick={() => registerMutation.mutate({ event_type: "break_end" })}
                  disabled={registerMutation.isPending}
                >
                  <CheckCircle2 className="w-6 h-6 mr-2" /> {t.timeTracking.buttons.resumeWork}
                </Button>
              )}
            </div>

            {/* NFC scan area */}
            <div className="border-t pt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Nfc className="w-4 h-4" /> {t.timeTracking.nfc.personalNfc}
                {nfcBridge.isReaderReady && (
                  <span className="ml-auto inline-flex items-center gap-1 text-green-600">
                    <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" /> {t.timeTracking.nfc.usbReaderReady}
                  </span>
                )}
              </div>
              {isMobileNfcSupported && (
                <Button variant="outline" className="w-full" onClick={handleScanMobile}>
                  <Nfc className="w-4 h-4 mr-2" /> {t.timeTracking.nfc.scanMobile}
                </Button>
              )}
              {!nfcBridge.isReaderReady && !isMobileNfcSupported && (
                <p className="text-xs text-muted-foreground">{t.timeTracking.nfc.connectReader}</p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Eventos de hoy */}
        <Card>
          <CardHeader>
            <CardTitle>{t.timeTracking.todayEvents}</CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <p className="text-muted-foreground text-sm">{t.common.loading}</p>
            ) : todayEvents.length === 0 ? (
              <p className="text-muted-foreground text-sm">{t.timeTracking.noEvents}</p>
            ) : (
              <ul className="space-y-2">
                {todayEvents.map((e) => {
                  const cfg = eventLabels[e.event_type];
                  const Icon = cfg?.icon || AlertCircle;
                  return (
                    <li key={e.id} className="flex items-center gap-3 p-2 rounded-md bg-muted/50">
                      <div className={`w-8 h-8 rounded-full ${cfg?.color || 'bg-gray-500'} text-white flex items-center justify-center flex-shrink-0`}>
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{cfg?.label || e.event_type}</span>
                          {e.source !== 'button' && <Badge variant="outline" className="text-xs">{e.source}</Badge>}
                          {e.is_corrected && <Badge variant="destructive" className="text-xs">corregido</Badge>}
                        </div>
                        <p className="text-xs text-muted-foreground font-mono">{format(new Date(e.event_at), "HH:mm:ss")}</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
